import Vue from 'vue';
import { MutationTree } from 'vuex';
import { VForm } from '@/features/common/types';
import { deepClone } from '@/features/common/helpers';
import {
  Shipment,
  PartItem,
  AddressBookItem,
  AvailableService,
  InProgress,
  StoreForImport,
  SignatureType,
  ReconnectAccounts,
  LandedCostResponse,
} from '../types';
import { UploadDocument, DocumentStatus } from '../types/documentType';
import { parseValidInProgressData } from '../helpers/createShipment';

const mutations: MutationTree<Shipment> = {
  setWhereShipmentForm(state: Shipment, value: VForm): void {
    state.whereShipmentForm = value;
  },

  setWhatShipmentForm(state: Shipment, value: VForm): void {
    state.whatShipmentForm = value;
  },

  setGeneralInfoForm(state: Shipment, value: VForm): void {
    state.generalInfoForm = value;
  },

  setShowNotification(state: Shipment, value: boolean): void {
    state.showNotification = value;
  },

  setShowSummaryPage(state: Shipment, value: boolean): void {
    state.showSummaryPage = value;
  },

  setSelectedService(state: Shipment, value: AvailableService): void {
    state.selectedService = deepClone(value);
  },

  setPartsSummary(state: Shipment, value: Array<PartItem>): void {
    const { commercialInvoice } = state.inProgress;
    if (!commercialInvoice) {
      state.inProgress.commercialInvoice = {
        customsParts: [],
        reasonForExport: '',
      };
    }

    state.inProgress.commercialInvoice.customsParts = [...value];
  },

  setAddressBook(state: Shipment, value: Array<AddressBookItem>) {
    state.addressBook = value;
  },

  addAddressBookItem(state: Shipment, value: AddressBookItem) {
    state.addressBook = [
      value,
      ...state.addressBook,
    ];
  },

  replaceAddressBookItem(state: Shipment, { idx, value }: { idx: number, value: AddressBookItem }) {
    state.addressBook[idx] = value;
  },

  deleteAddressBookItem(state: Shipment, idx: number) {
    state.addressBook.splice(idx, 1);
  },

  setAvailableServices(state: Shipment, value: Array<AvailableService>) {
    state.availableServices = value;
  },

  setReconnectAccountRequired(state: Shipment, value: ReconnectAccounts) {
    state.reconnectAccountRequired = value;
  },

  incrementIsInProgress(state: Shipment) {
    state.isInProgressData += 1;
  },

  setInProgress(state: Shipment, value: InProgress) {
    if (!value) {
      state.isInProgressData = 0;
    } else {
      state.inProgress = parseValidInProgressData(value);
      state.isInProgressData += 1;
    }
  },

  setInProgressSilently(state: Shipment, value: InProgress) {
    state.inProgress = parseValidInProgressData(value);
  },

  patchInProgress(state: Shipment, { key, value }: { key: string, value: never }) {
    const keys = key.split('/');
    switch (keys.length) {
      case 1:
        state.inProgress[keys[0]] = value;
        break;
      case 2:
        state.inProgress[keys[0]][keys[1]] = value;
        break;
      default:
        break;
    }
    state.isInProgressData += 1;
  },

  updateInProgressPackagesSignature(state: Shipment, signature: keyof typeof SignatureType) {
    state.inProgress.signatureOptions = signature;
    state.inProgress.packages = state.inProgress.packages.map((packageItem) => {
      return {
        ...packageItem,
        signatureOptions: signature,
      };
    });
  },

  setStoresForImport(state: Shipment, value: Array<StoreForImport>) {
    state.storesForImport = value;
  },

  setSecureshipTrackingNo(state: Shipment, value: string) {
    state.secureshipTrackingNo = value;
  },

  setChangeInProgressData(state: Shipment, isChanged: boolean) {
    state.isInProgressDataChanged = isChanged;
  },

  setUploadDocuments(state: Shipment, value: Array<UploadDocument>): void {
    state.inProgress.uploadDocuments = [...value];
  },

  addUploadDocument(state: Shipment, value: UploadDocument): void {
    if (!state.inProgress.uploadDocuments) {
      state.inProgress.uploadDocuments = [];
    }
    state.inProgress.uploadDocuments.push(value);
  },

  replaceDocument(
    state: Shipment,
    { oldDocumentId, newDocument }: { oldDocumentId: string; newDocument: UploadDocument },
  ): void {
    const docs = state.inProgress.uploadDocuments;
    if (!docs) return;

    const idx = docs.findIndex((d) => d.documentId === oldDocumentId);
    if (idx !== -1) {
      docs.splice(idx, 1, newDocument);
    }
  },

  updateDocumentStatus(
    state: Shipment,
    { documentId, status, failedReason }: {
      documentId: string; status: DocumentStatus; failedReason?: string;
    },
  ): void {
    const docs = state.inProgress.uploadDocuments;
    if (!docs) return;

    const doc = docs.find((d) => d.documentId === documentId);
    if (doc) {
      doc.status = status;
      if (failedReason) {
        doc.failedReason = failedReason;
      }
    }
  },

  updateDocumentType(
    state: Shipment,
    { documentId, documentType }: { documentId: string; documentType: string },
  ): void {
    const docs = state.inProgress.uploadDocuments;
    if (!docs) return;

    const doc = docs.find((d) => d.documentId === documentId);
    if (doc) {
      doc.documentType = documentType;
    }
  },

  removeDocument(state: Shipment, documentId: string): void {
    const docs = state.inProgress.uploadDocuments;
    if (!docs) return;

    const idx = docs.findIndex((d) => d.documentId === documentId);
    if (idx !== -1) {
      docs.splice(idx, 1);
    }
  },

  setServiceLandedCost(
    state: Shipment,
    { serviceId, landedCost }: {
      serviceId: number | undefined;
      landedCost: LandedCostResponse | null;
    },
  ): void {
    if (!state.availableServices) return;
    const service = state.availableServices.find((s) => s.id === serviceId);
    if (!service) return;
    Vue.set(service, 'landedCost', landedCost);
  },
};

export default mutations;
